import {
  DETAIL_ANIMAL_SEARCH_TYPES,
  SEARCH_TYPE,
} from '@/constants/searchType';
import { OperatorOption } from '../constants';
import {
  expandAnimalIdentChip,
  hasWildcardChars,
  isEdgeOnlyWildcardPattern,
  normalizeWildcardInput,
} from './animalIdentChipFilter';

export type HeroSearchGuidance = {
  kind: 'multiIdent' | 'wildcard';
  message: string;
  showOpenBasicSearch: boolean;
};

function supportsMultiIdentHint(searchType: string): boolean {
  return (
    searchType === SEARCH_TYPE.ANIMAL ||
    searchType === SEARCH_TYPE.MEMBERS ||
    DETAIL_ANIMAL_SEARCH_TYPES.includes(searchType)
  );
}

function multiIdentMessage(searchType: string, count: number): string {
  const subject =
    searchType === SEARCH_TYPE.MEMBERS ? 'member idents' : 'idents';
  if (count <= 2) {
    return `Searching multiple ${subject} (any match)`;
  }
  return `Searching any of ${count} ${subject}`;
}

function wildcardMatchLabel(operator: string): string | null {
  switch (operator) {
    case OperatorOption.startsWith.value:
      return 'Starts with';
    case OperatorOption.contains.value:
      return 'Contains';
    case OperatorOption.endsWith.value:
      return 'Ends with';
    default:
      return null;
  }
}

/**
 * Contextual helper copy for the hero quick-search box. Does not change
 * search behaviour — guidance only (ILRX-4073).
 *
 * Priority: comma-separated multi-ident confirmation, then wildcard labels.
 */
export function getHeroSearchGuidance(
  value: string,
  searchType: string,
): HeroSearchGuidance | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (supportsMultiIdentHint(searchType) && trimmed.includes(',')) {
    const count = trimmed
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean).length;
    if (count >= 2) {
      return {
        kind: 'multiIdent',
        message: multiIdentMessage(searchType, count),
        showOpenBasicSearch: false,
      };
    }
  }

  if (!hasWildcardChars(trimmed)) return null;

  const normalized = normalizeWildcardInput(trimmed);
  if (!normalized.includes('%')) return null;

  if (isEdgeOnlyWildcardPattern(normalized)) {
    const exp = expandAnimalIdentChip(normalized);
    const label = wildcardMatchLabel(exp.operator);
    if (label && exp.value) {
      return {
        kind: 'wildcard',
        message: `${label} ${exp.value}`,
        showOpenBasicSearch: false,
      };
    }
  }

  return {
    kind: 'wildcard',
    message: 'Wildcard pattern recognized',
    showOpenBasicSearch: false,
  };
}
